<template>
    <div class="connector-settings-sidebar-content">
        <v-row class="ma-0 mb-4 header-row" align="center" justify="space-between">
            <div class="header-row__main">
                <h2 class="dashboard">
                    {{ connectorType === 'whatsappBasis' ? 'Настройки бота' : connectorAvatar ? (isChannel ? `Настройки канала «${connectorAvatar.name}»` : isTelephony ? `Настройки телефонии «${connectorAvatar.name}»` : `Настройки источника «${connectorAvatar.name}»`) : 'Загрузка' }}
                </h2>
                <HintIcon v-if="hintId" :hintId="hintId" class="ml-2"></HintIcon>
            </div>
            <v-btn
                icon
                class="close-btn"
                aria-label="Закрыть"
                @click="cancel"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-row>
        <!-- ID коннектора для суперпользователя -->
        <div
            v-if="isSuperUser && connector && connector.connectorId"
            class="connector-id-wrapper mb-4"
        >
            <div class="connector-id-container">
                <span class="connector-id-label">ID:</span>
                <code class="connector-id-value">{{ connector.connectorId }}</code>
                <v-tooltip bottom :value="copyTooltip" :open-on-hover="false">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            x-small
                            class="connector-id-copy-btn"
                            @click="copyConnectorId"
                            v-bind="attrs"
                        >
                            <v-icon small :color="copyTooltip ? '#4CAF50' : '#378EFF'">
                                {{ copyTooltip ? 'mdi-check' : 'mdi-content-copy' }}
                            </v-icon>
                        </v-btn>
                    </template>
                    <span class="white--text">Скопировано</span>
                </v-tooltip>
            </div>
        </div>
        <!-- Для каналов используем ChannelSettingsForm -->
        <div
            v-if="isChannel && (!providerType || isLoadingChannelSettings || !isChannelSettingsReady)"
            class="channel-settings-sidebar-loader"
        >
            <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
        </div>
        <ChannelSettingsForm
            v-if="isChannel && providerType"
            v-show="isChannelSettingsReady"
            :key="`channel-form-${connectorId}-${providerType}`"
            :value="channelSettings"
            :connector="connector"
            :providerType="providerType"
            :appCreated="appCreated"
            :mode="'edit'"
            :inSidebar="true"
            @input="channelSettings = $event"
            @saved="cancel"
            @auth-status-changed="handleChannelAuthStatusChanged"
            @ready="isChannelSettingsReady = true"
        />
        <!-- Для остальных источников используем стандартные компоненты -->
        <component
            v-else-if="!isChannel && activeComponent"
            @cancel="cancel"
            @changeSettings="changeSettings"
            :is="activeComponent"
            :isChange="true"
            :connector="connector"
        ></component>

        <h2 class="dashboard" v-else-if="!isChannel">
            {{ connectorAvatar ? 'В настоящее время настройки в этом источнике не поддерживаются' : '' }}
        </h2>

        <!-- Блок с информацией о настройках отправки -->
        <div v-if="isChannel && isChannelSettingsReady" class="notification-settings-info mt-2 pt-6" style="border-top: 1px solid #E0E0E0;">
            <div class="mb-3" style="color: rgba(42, 59, 81, 0.87); font-size: 14px; line-height: 20px;">
                {{ notificationSettingsText }}
            </div>
            <v-btn
                text
                color="mainNew"
                @click="goToNotificationSettings"
                class="text-decoration-none pa-0"
                style="min-width: auto; height: auto; cursor: pointer;"
            >
                перейти
                <v-icon small class="ml-1">mdi-open-in-new</v-icon>
            </v-btn>
        </div>

        <!-- Блок удаления канала для владельца -->
        <div v-if="isChannel && isOwner && isChannelSettingsReady && !isMaxBusinessChannel" class="delete-channel-info mt-8 pt-6" style="border-top: 1px solid #E0E0E0;">
            <v-btn
                text
                color="error"
                @click="showDeleteConfirm = true"
                class="text-decoration-none pa-0"
                style="min-width: auto; height: auto; cursor: pointer;"
            >
                <v-icon small class="mr-1">mdi-delete-outline</v-icon>
                Удалить канал
            </v-btn>
        </div>

        <!-- Диалог подтверждения удаления -->
        <DeleteConnectorConfirm
            v-model="showDeleteConfirm"
            :connectors="[connectorId]"
        />
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Markers from '@/components/addConnector/Markers.vue'
import HintIcon from '@/components/HintIcon.vue'
import settingsList from '@/components/connectorSettings/list'
import { CONNECTOR_TYPES, CHANNEL_TYPES, PROVIDER_TYPES, TELEPHONY_CONNECTOR_TYPES } from '@/vars/general.js'
import DeleteConnectorConfirm from '@/components/project/dialogs/DeleteConnectorConfirm.vue'
import cfg from '@root/config.js'

export default {
    name: 'ConnectorSettingsSidebarContent',
    components: {
        HintIcon,
        Markers,
        ChannelSettingsForm: () => import('@/components/channels/ChannelSettingsForm.vue'),
        DeleteConnectorConfirm,
    },
    props: {
        connectorId: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        CONNECTOR_TYPES,
        CHANNEL_TYPES,
        TELEPHONY_CONNECTOR_TYPES,
        changedSettingsImportance: null,
        additionalSettingsData: null,
        settings: null,
        channelSettings: {},
        isLoadingChannelSettings: false,
        isChannelSettingsReady: false,
        appCreated: true,
        yCSimpleAuth: cfg.yClientSimpleAuth,
        showDeleteConfirm: false,
        copyTooltip: false,
    }),
    computed: {
        project() {
            // Получаем projectId из текущего роута или из connector
            const projectId = this.$router.currentRoute.params.id || 
                             (this.connector && this.connector.projectId)
            return projectId ? this.$store.getters.projectById(projectId) : null
        },
        hintId() {
            let hintId = null
            if (this.connectorType === CONNECTOR_TYPES.yClients) {
                //hintId = 'ycSettings'
            }
            return hintId
        },
        connector() {
            return this.$store.getters['connectorById'](this.connectorId)
        },
        connectorType() {
            return this.connector ? this.connector.connectorType : null
        },
        connectorAvatar() {
            return this.connector
                ? this.$store.getters['connectorAvatarByType'](this.connector.connectorType)
                : null
        },
        settingsImportance() {
            return this.connector
                ? this.$store.getters['connectorSettingsImportanceByType'](this.connector.connectorType)
                : null
        },
        activeComponent() {
            return settingsList.find(el => el.connectorType === this.connector?.connectorType)?.component
        },
        isChannel() {
            if (!this.connector) return false
            // Проверяем, является ли connectorType каналом
            const channelTypes = Object.values(CHANNEL_TYPES)
            return channelTypes.includes(this.connector.connectorType)
        },
        isTelephony() {
            if (!this.connector) return false
            // Проверяем, является ли connectorType телефонией
            return TELEPHONY_CONNECTOR_TYPES.includes(this.connector.connectorType)
        },
        providerType() {
            if (!this.connector) return null
            // Получаем providerType из коннектора
            return this.connector.providerType || null
        },
        hasYClientsConnector() {
            if (!this.project || !this.project.connectors) return false
            return this.project.connectors.some(
                (con) => con.connectorType === CONNECTOR_TYPES.yClients
            )
        },
        notificationSettingsText() {
            if (this.hasYClientsConnector) {
                return 'Настройки отправки (название салона, адрес салона, режим работы бота, номер на который будут отправляться сервисные уведомления и т. п. находятся во вкладке "Настройки отправки"'
            } else {
                return 'Настройки отправки (название салона, адрес салона и т. п. находятся во вкладке "Настройки отправки"'
            }
        },
        isSuperUser() {
            return cfg.adminsWhitelist.includes(this.$auth.user.sub)
        },
        isOwner() {
            return this.project?.myRole === 'owner'
        },
        isMaxBusinessChannel() {
            return (
                this.connectorType === CHANNEL_TYPES.maxBusiness ||
                this.providerType === PROVIDER_TYPES.maxBusiness
            )
        },
    },
    watch: {
        connectorId: {
            immediate: true,
            handler(newId, oldId) {
                // Сбрасываем состояние при смене коннектора
                if (newId !== oldId && oldId !== undefined) {
                    this.resetState()
                    this.$nextTick(() => {
                        if (this.isChannel && this.connector) {
                            this.loadChannelSettings()
                        }
                    })
                }
            }
        },
        connector: {
            immediate: true,
            handler(newConnector, oldConnector) {
                // Загружаем настройки канала при изменении коннектора
                if (newConnector && this.isChannel && oldConnector && newConnector.connectorId !== oldConnector.connectorId) {
                    this.loadChannelSettings()
                } else if (newConnector && this.isChannel && !oldConnector) {
                    // Первая загрузка
                    this.loadChannelSettings()
                }
            }
        },
        showDeleteConfirm(newValue) {
            // Если диалог закрыт, проверяем, был ли коннектор удален
            if (!newValue) {
                this.$nextTick(() => {
                    const connector = this.$store.getters['connectorById'](this.connectorId)
                    if (!connector) {
                        // Коннектор был удален, закрываем сайдбар
                        this.cancel()
                    }
                })
            }
        }
    },
    methods: {
        resetState() {
            // Сбрасываем все состояние компонента
            this.channelSettings = {}
            this.isLoadingChannelSettings = false
            this.isChannelSettingsReady = false
            this.changedSettingsImportance = null
            this.additionalSettingsData = null
            this.settings = null
        },
        async loadChannelSettings() {
            if (!this.connector) return
            
            this.isLoadingChannelSettings = true
            try {
                // Загружаем настройки канала из коннектора
                this.channelSettings = this.connector.settings || {}
            } catch (error) {
                console.error('Ошибка при загрузке настроек канала:', error)
                this.channelSettings = {}
            } finally {
                this.isLoadingChannelSettings = false
            }
        },
        async changeSettings({ prevSettings, settings, additionalData }) {
            // Сохраняем настройки и ждем завершения (включая запрос на получение коннектора)
            await this.$store.dispatch('project/updateConnectorSettings', {
                connectorId: this.connector.connectorId,
                settings: settings,
            })
            this.cancel()
        },
        cancel() {
            this.$emit('close')
        },
        handleChannelAuthStatusChanged(payload) {
            this.$root.$emit('reload-channel-auth-status', payload)
        },
        goToNotificationSettings() {
            // Закрываем сайдбар и переключаемся на вкладку "Настройки отправки"
            this.cancel()
            this.$nextTick(() => {
                this.$root.$emit('switch-to-notification-settings-tab')
            })
        },
        async copyConnectorId() {
            const connectorId = this.connector?.connectorId
            if (!connectorId || this.copyTooltip) {
                return
            }
            try {
                await navigator.clipboard.writeText(connectorId)
                this.copyTooltip = true
                setTimeout(() => {
                    this.copyTooltip = false
                }, 3000)
            } catch (err) {
                console.error('Ошибка при копировании ID:', err)
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.connector-settings-sidebar-content
    padding: 0

h2.dashboard
    font-weight: 600 !important
    font-size: 20px !important
    color: #2A3B51
    margin-bottom: 0 !important
    letter-spacing: -0.3px
    min-width: 0
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.header-row
    padding: 0 0 16px 0
    border-bottom: 1px solid #E1E8ED
    margin-bottom: 20px !important
    flex-wrap: nowrap !important
    max-width: 100%

.header-row__main
    display: flex
    align-items: center
    flex: 1
    min-width: 0

.close-btn
    flex-shrink: 0
    margin-left: 8px

.channel-settings-sidebar-loader
    display: flex
    align-items: center
    justify-content: center
    min-height: 200px
    width: 100%

.connector-id-wrapper
    margin-top: -12px
    margin-bottom: 16px !important
    padding: 0 0 10px 0
    border-bottom: 1px solid #E1E8ED

.connector-id-container
    display: flex
    align-items: center
    gap: 8px
    padding: 6px 12px
    background: #F5F7FA
    border-radius: 6px
    border: 1px solid #E1E8ED
    transition: all 0.2s ease

.connector-id-label
    font-size: 12px
    font-weight: 500
    color: #2A3B5199
    letter-spacing: 0.2px

.connector-id-value
    flex: 1
    font-family: monospace
    font-size: 12px
    color: #2A3B51
    background: transparent
    padding: 0
    padding-left: 5px
    border: none
    letter-spacing: 0.3px

.connector-id-copy-btn
    min-width: 24px !important
    width: 24px !important
    height: 24px !important
</style>

